'use client'

import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getExperienceById, experiences } from '@/lib/data'
import BookNowCTA from '@/components/BookNowCTA'
import adventureStyles from '@/styles/adventure-page.module.css'
import styles from '@/styles/experience-page.module.css'
import { BreadcrumbSchema } from '@/lib/schema'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import '@/components/Adventures.css'


export default function PrivateDinnersSantiagoPage() {
  const thisExperience = getExperienceById('private-dinners-santiago')
  const t = useTranslations('experiences')
  const tCommon = useTranslations('common')
  const params = useParams()
  const locale = params.locale as string

  const relatedExperiences = experiences.filter(e => e.id !== 'private-dinners-santiago').slice(0, 2)

  const mainRef = useScrollAnimation<HTMLElement>({
    threshold: 0.12,
    rootMargin: '0px 0px -60px 0px',
  })

  if (!thisExperience) {
    return <div>{t('common.experienceNotFound')}</div>
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/experiences/` },
        { name: t('cards.private-dinners-santiago.title'), href: `/${locale}/experiences/private-dinners-santiago/` },
      ]} />
      <link rel="preload" as="image" href={thisExperience.heroImage} fetchPriority="high" />
      <Header />

      <HeroParallax
        backgroundImage={thisExperience.heroImage}
        title={t('cards.private-dinners-santiago.title')}
        subtitle={t('cards.private-dinners-santiago.subtitle')}
        location={t('cards.private-dinners-santiago.location')}
      />

      <main className={adventureStyles.pageWrapper} ref={mainRef}>
        <section className={adventureStyles.adventureDetails}>
          <div className="container">
            <div className={adventureStyles.detailsGrid}>
              <div className={adventureStyles.itineraryColumn} data-scroll-item>
                <h3>{t('privateDinners.itineraryTitle')}</h3>
                <h3>{t('privateDinners.itinerarySubtitle')}</h3>
                <p className={adventureStyles.serviceDescription}>{t('privateDinners.descriptionText')}</p>
              </div>

              <div className={adventureStyles.mapColumn} data-scroll-item style={{ transitionDelay: '150ms' }}>
                <div className={adventureStyles.mapWrapper}>
                  <div style={{
                    height: '480px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  }}>
                    <img
                      src="/images/experiences/private-dinners/dinner-feature.webp"
                      alt={t('privateDinners.alts.feature')}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      loading="eager"
                    />
                  </div>
                </div>
              </div>

              <div className={adventureStyles.includedColumn} data-scroll-item style={{ transitionDelay: '300ms' }}>
                <h3>{t('privateDinners.includedTitle')}</h3>
                <ul>
                  <li><strong>{t('privateDinners.included.item1Title')}</strong> {t('privateDinners.included.item1')}</li>
                  <li><strong>{t('privateDinners.included.item2Title')}</strong> {t('privateDinners.included.item2')}</li>
                  <li><strong>{t('privateDinners.included.item3Title')}</strong> {t('privateDinners.included.item3')}</li>
                  <li><strong>{t('privateDinners.included.item4Title')}</strong> {t('privateDinners.included.item4')}</li>
                  <li><strong>{t('privateDinners.included.item5Title')}</strong> {t('privateDinners.included.item5')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div data-scroll-item>
          <BookNowCTA
            variant="inline"
            productId="private-dinners-santiago"
            productType="experience"
            productName={t('privateDinners.productName')}
            bookingMode={thisExperience.bookingMode || 'whatsapp'}
          />
        </div>

        <section className={styles.howItWorksSection}>
          <div className={styles.howItWorksContainer}>
            <div className={styles.luxuryHowCard}>
              <div className={styles.luxuryHowContent} data-scroll-item>
                <span className={styles.luxuryHowOverline}>{t('privateDinners.howItWorks.eyebrow')}</span>
                <h2
                  className={styles.luxuryHowTitle}
                  dangerouslySetInnerHTML={{ __html: t.raw('privateDinners.howItWorks.title') as string }}
                />
                <p className={styles.luxuryHowTagline}>{t('privateDinners.howItWorks.tagline')}</p>

                <ol className={styles.luxuryStepsList}>
                  {[1, 2, 3, 4].map((n, i) => (
                    <li key={n} className={styles.luxuryStepItem} data-scroll-item style={{ transitionDelay: `${i * 110}ms` }}>
                      <span className={styles.luxuryStepNum}>{String(n).padStart(2, '0')}</span>
                      <div className={styles.luxuryStepCopy}>
                        <h3 className={styles.luxuryStepHeading}>{t(`privateDinners.howItWorks.step${n}Title`)}</h3>
                        <p className={styles.luxuryStepDesc}>{t(`privateDinners.howItWorks.step${n}Text`)}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className={styles.luxuryHowImageWrapper} data-scroll-item style={{ transitionDelay: '180ms' }}>
                <img
                  src="/images/experiences/private-dinners/dinner-gallery-11.webp"
                  alt={t('privateDinners.alts.celebration')}
                  className={styles.luxuryHowImage}
                  loading="lazy"
                />
                <div className={styles.luxuryHowImageCaption}>
                  <span>{t('privateDinners.howItWorks.imageCaption')}</span>
                </div>
              </div>
            </div>

            <div className={styles.luxuryOccasionsCallout} data-scroll-item>
              <p
                className={styles.luxuryOccasionsText}
                dangerouslySetInnerHTML={{ __html: t.raw('privateDinners.howItWorks.occasionsText') as string }}
              />
            </div>
          </div>
        </section>

        <section className={styles.luxuryOptionalSection}>
          <div className={styles.luxuryOptionalContainer}>
            <div className={styles.luxuryOptionalCard}>
              <div className={styles.luxuryOptionalImageWrapper} data-scroll-item>
                <img
                  src="/images/experiences/private-dinners/dinner-gallery-05.webp"
                  alt={t('privateDinners.alts.optional')}
                  className={styles.luxuryOptionalImage}
                  loading="lazy"
                />
                <div className={styles.luxuryOptionalImageCaption}>
                  <span>{t('privateDinners.optional.imageCaption')}</span>
                </div>
              </div>

              <div className={styles.luxuryOptionalContent} data-scroll-item style={{ transitionDelay: '160ms' }}>
                <span className={styles.luxuryOptionalOverline}>{t('privateDinners.optional.overline')}</span>
                <h2
                  className={styles.luxuryOptionalTitle}
                  dangerouslySetInnerHTML={{ __html: t.raw('privateDinners.optional.title') as string }}
                />
                <p className={styles.luxuryOptionalTagline}>{t('privateDinners.optional.tagline')}</p>

                <ul className={styles.luxuryOptionalList}>
                  <li className={styles.luxuryOptionalItem} data-scroll-item style={{ transitionDelay: '0ms' }}>
                    <span className={styles.luxuryOptionalItemIcon}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 2v7a4 4 0 0 0 8 0V2" />
                        <path d="M12 13v8" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                      </svg>
                    </span>
                    <div className={styles.luxuryOptionalItemBody}>
                      <h3 className={styles.luxuryOptionalItemTitle}>{t('privateDinners.optional.item1Title')}</h3>
                      <p className={styles.luxuryOptionalItemText}>{t('privateDinners.optional.item1')}</p>
                    </div>
                  </li>

                  <li className={styles.luxuryOptionalItem} data-scroll-item style={{ transitionDelay: '90ms' }}>
                    <span className={styles.luxuryOptionalItemIcon}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18V5l12-2v13" />
                        <circle cx="6" cy="18" r="3" />
                        <circle cx="18" cy="16" r="3" />
                      </svg>
                    </span>
                    <div className={styles.luxuryOptionalItemBody}>
                      <h3 className={styles.luxuryOptionalItemTitle}>{t('privateDinners.optional.item2Title')}</h3>
                      <p className={styles.luxuryOptionalItemText}>{t('privateDinners.optional.item2')}</p>
                    </div>
                  </li>

                  <li className={styles.luxuryOptionalItem} data-scroll-item style={{ transitionDelay: '180ms' }}>
                    <span className={styles.luxuryOptionalItemIcon}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="5" width="18" height="14" rx="2" ry="2" />
                        <circle cx="12" cy="12" r="3" />
                        <path d="M17 8.5h.01" />
                      </svg>
                    </span>
                    <div className={styles.luxuryOptionalItemBody}>
                      <h3 className={styles.luxuryOptionalItemTitle}>{t('privateDinners.optional.item3Title')}</h3>
                      <p className={styles.luxuryOptionalItemText}>{t('privateDinners.optional.item3')}</p>
                    </div>
                  </li>

                  <li className={styles.luxuryOptionalItem} data-scroll-item style={{ transitionDelay: '270ms' }}>
                    <span className={styles.luxuryOptionalItemIcon}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                      </svg>
                    </span>
                    <div className={styles.luxuryOptionalItemBody}>
                      <h3 className={styles.luxuryOptionalItemTitle}>{t('privateDinners.optional.item4Title')}</h3>
                      <p className={styles.luxuryOptionalItemText}>{t('privateDinners.optional.item4')}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.gallery}>
          <div className="container">
            <div className={styles.sectionHeader} data-scroll-item>
              <h2 className={styles.sectionTitle}>{t('privateDinners.galleryTitle')}</h2>
              <p className={styles.sectionDescription}>
                {t('privateDinners.galleryDescription')}
              </p>
            </div>

            <div className={styles.galleryGrid}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((n, i) => {
                const num = String(n).padStart(2, '0')
                return (
                  <div key={n} className={styles.galleryItem} data-scroll-item style={{ transitionDelay: `${i * 70}ms` }}>
                    <img
                      src={`/images/experiences/private-dinners/dinner-gallery-${num}.webp`}
                      alt={t(`privateDinners.alts.gallery${num}`)}
                      loading="lazy"
                    />
                  </div>
                )
              })}
            </div>

            <div className={styles.sectionCta} data-scroll-item>
              <a href={`/${locale}/experiences/`} className="btn-primary">
                {t('common.exploreExperiences')} &rarr;
              </a>
            </div>
          </div>
        </section>

        <section className={styles.relatedSection}>
          <div className="container">
            <h2 data-scroll-item>{t('common.relatedExperiences')}</h2>
            <div className={styles.relatedGrid}>
              {relatedExperiences.map((experience, i) => {
                const cardTitle = t(`cards.${experience.id}.title`)
                const cardSubtitle = t(`cards.${experience.id}.subtitle`)
                return (
                  <article key={experience.id} className={styles.relatedCard} data-scroll-item style={{ transitionDelay: `${120 + i * 120}ms` }}>
                    <div className={styles.relatedImage}>
                      <img
                        src={experience.heroImage}
                        alt={cardTitle}
                        loading="lazy"
                      />
                    </div>
                    <div className={styles.relatedOverlay}>
                      <h3 className={styles.relatedTitle}>{cardTitle}</h3>
                      <div className={styles.relatedContent}>
                        <p>{cardSubtitle}</p>
                        <a href={`/${locale}/experiences/${experience.id}/`} className={styles.relatedLink}>
                          {t('common.explore')} &rarr;
                        </a>
                      </div>
                    </div>
                  </article>
                )
              })}
            </div>
          </div>
        </section>

        <div data-scroll-item>
          <BookNowCTA
            variant="closing"
            productId="private-dinners-santiago"
            productType="experience"
            productName={t('privateDinners.productName')}
            bookingMode={thisExperience.bookingMode || 'whatsapp'}
          />
        </div>
      </main>

      <Footer />
    </>
  )
}
